/**
 * ResponsiveImage — serves the right-sized WebP variant per breakpoint.
 *
 * Given a single URL the admin uploaded, derives the desktop / mobile WebP
 * variants generated by /api/uploads/image (suffix `_desktop.webp` and
 * `_mobile.webp`). Falls back to the original on older browsers / missing
 * variants. Always renders the image with `object-fit: cover` and
 * `object-position: center` so it crops gracefully across any aspect ratio
 * without stretching or zooming awkwardly.
 *
 * Recommended container patterns:
 *   • Hero/announcement banner — fixed aspect-ratio or fixed height container
 *   • Centre/event/coupon cover — fixed height container
 *
 * Props:
 *   src         — original uploaded URL (e.g. "/api/uploads/abc.png")
 *   alt         — image alt text
 *   className   — applied to the inner <img>
 *   sizes       — HTML `sizes` attribute, default "100vw"
 *   testid      — applied to <picture>
 *   eager       — set to true for above-the-fold heroes (default false)
 */
import { useMemo, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function absolutise(url) {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${BACKEND}${url}`;
}

function deriveVariants(src) {
    if (!src) return { desktop: "", mobile: "", original: "" };
    const original = absolutise(src);
    try {
        const m = src.match(/^(.+\/uploads\/[^./]+)\.[^/]+$/);
        if (!m) return { desktop: "", mobile: "", original };
        const baseRel = m[1];
        return {
            desktop: absolutise(`${baseRel}_desktop.webp`),
            mobile: absolutise(`${baseRel}_mobile.webp`),
            original,
        };
    } catch {
        return { desktop: "", mobile: "", original };
    }
}

export default function ResponsiveImage({
    src,
    alt = "",
    className = "w-full h-full object-cover object-center",
    sizes = "100vw",
    testid,
    eager = false,
}) {
    const variants = useMemo(() => deriveVariants(src), [src]);
    // If a variant fails to load, drop all <source>s and let <img> serve the original
    const [variantsFailed, setVariantsFailed] = useState(false);
    if (!variants.original) return null;

    const showSources = !variantsFailed && (variants.mobile || variants.desktop);
    const handleError = () => {
        // <img> fallback failed. Force a re-render that drops <source>s (no-op
        // if they're already absent) and try once more with the original URL.
        if (!variantsFailed) setVariantsFailed(true);
    };

    return (
        <picture data-testid={testid}>
            {showSources && variants.mobile && (
                <source media="(max-width: 768px)" srcSet={variants.mobile} type="image/webp" />
            )}
            {showSources && variants.desktop && (
                <source media="(min-width: 769px)" srcSet={variants.desktop} type="image/webp" />
            )}
            <img
                src={variants.original}
                alt={alt}
                className={className}
                loading={eager ? "eager" : "lazy"}
                decoding="async"
                draggable="false"
                sizes={sizes}
                onError={handleError}
            />
        </picture>
    );
}
